/**
 * useWalletTransactions — W1.5 + F-E.2
 *
 * Paginierte Transaktions-Historie fuer eine Wallet.
 * Error-Isolation: wirft Fehler nicht nach oben — Error-Boundary in
 * WalletTransactions-Komponente faengt ab.
 * Load-More: page-State, akkumulierte Transactions.
 * F-E.2: Optionale Filter-Parameter (type, from, to).
 */
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../lib/api-client'

// --- Lokale Typen ---

export type TransactionDirection = 'in' | 'out' | 'unknown'

export type WalletTransaction = {
  signature: string
  type: string
  direction: TransactionDirection
  token_symbol: string
  amount: number
  timestamp: string
}

export type TransactionsMeta = {
  page: number
  per_page: number
  has_more: boolean
}

export type WalletTransactionsResponse = {
  data: WalletTransaction[]
  meta: TransactionsMeta
}

export type TransactionFilters = {
  type?: 'in' | 'out' | 'reward' | ''
  from?: string
  to?: string
}

// --- Fetch-Helper ---

async function fetchTransactions(
  walletId: string,
  page: number,
  filters: TransactionFilters,
): Promise<WalletTransactionsResponse> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const client = apiClient as any

  const query: Record<string, string | number> = { page }
  if (filters.type) query['type'] = filters.type
  if (filters.from) query['from'] = filters.from
  if (filters.to) query['to'] = filters.to

  const result = await client.GET('/api/v2/wallets/{id}/transactions', {
    params: {
      path: { id: walletId },
      query,
    },
  })
  if (result.error) throw result.error
  return result.data as WalletTransactionsResponse
}

// --- Hook ---

type UseWalletTransactionsResult = {
  transactions: WalletTransaction[]
  isLoading: boolean
  isError: boolean
  hasMore: boolean
  loadMore: () => void
}

export function useWalletTransactions(
  walletId: string,
  filters: TransactionFilters = {},
): UseWalletTransactionsResult {
  const [page, setPage] = useState(1)
  const [accumulated, setAccumulated] = useState<WalletTransaction[]>([])
  const [hasMore, setHasMore] = useState(false)

  // Filter-Aenderung setzt Paginierung zurueck
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPage(1)
    setAccumulated([])
    setHasMore(false)
  }, [filters.type, filters.from, filters.to])

  const { isLoading, isError } = useQuery({
    queryKey: ['wallet', walletId, 'transactions', page, filters.type, filters.from, filters.to],
    queryFn: async () => {
      const response = await fetchTransactions(walletId, page, filters)
      if (page === 1) {
        setAccumulated(response.data)
      } else {
        setAccumulated((prev) => [...prev, ...response.data])
      }
      setHasMore(response.meta.has_more)
      return response
    },
    // Kein retry bei Fehler — Transactions sind optional
    retry: false,
    // Stale-Time hoch — Transactions aendern sich selten waehrend Session
    staleTime: 60_000,
  })

  function loadMore() {
    setPage((p) => p + 1)
  }

  return {
    transactions: accumulated,
    isLoading,
    isError,
    hasMore,
    loadMore,
  }
}
